import React, { useEffect, useState, useMemo } from 'react';
import { Link } from 'react-router-dom';
import { getExecutiveEditors } from '../services/firebaseService';
import { FaBriefcase, FaMapMarkerAlt, FaUniversity, FaEnvelope, FaGraduationCap, FaLink, FaUserCircle, FaExternalLinkAlt } from 'react-icons/fa';
import { usePageContent } from '../services/pageContentService';
import RawHtmlPage, { isRawHtmlPage } from '../components/RawHtmlPage';
import { normalizeUrl } from '../utils/urlUtils';

// Guaranteed fallback for Dr. Dheeraj Pandey
const defaultEditorInChief = {
  name: 'Dr. Dheeraj Pandey',
  role: 'Editor-in-Chief',
  memberDesignation: 'Professor & Head of Department (Practice of Medicine)',
  designation: 'Professor & Head of Department (Practice of Medicine)',
  qualification: 'BHMS, MD (Hom.) – Practice of Medicine',
  address: 'Aarya-Veer Homoeopathic Medical College & Hospital, Kankot, Rajkot - 360005, Gujarat, India',
  institution: 'Aarya-Veer Homoeopathic Medical College & Hospital, India',
  email: 'drdheeraj@avhmc.org',
  photo: 'https://firebasestorage.googleapis.com/v0/b/domushomoeopathica-b6397.firebasestorage.app/o/AJHM%2FEditorial%20Board%2FDr%20Dheeraj%20Pandey.jpg?alt=media&token=f5cc14c2-c9fe-4bcb-a8b6-d7079d830cce',
};

// Member's profile links from the admin "Scholarly / Social Links" field, labelled by platform name
// (or the site's domain when no platform is given). Empty entries are skipped.
// One style for every detail line on a member card: same size, weight and icon alignment.
// Black and grey only: name black, details grey, icons light grey, links grey (black + underline on hover).
const DETAIL_LINK = 'text-gray-600 underline decoration-gray-300 underline-offset-2 hover:text-gray-900 hover:decoration-gray-900 break-all';

const DetailRow = ({ icon: Icon, children }) => (
  <div className="flex items-start gap-2.5 text-sm text-gray-600 leading-snug">
    <Icon className="w-3.5 h-3.5 text-gray-400 flex-shrink-0 mt-0.5" aria-hidden="true" />
    <div className="min-w-0">{children}</div>
  </div>
);

const getProfileLinks = (member) =>
  (Array.isArray(member.social) ? member.social : [])
    .map((s) => {
      const url = normalizeUrl(s?.url);
      if (!url) return null;
      let label = (s.icon || '').trim();
      if (!label) {
        try {
          label = new URL(url).hostname.replace(/^www\./, '');
        } catch (e) {
          label = 'Profile';
        }
      }
      return { url, label };
    })
    .filter(Boolean);

const EditorialBoardPage = () => {
  const { content: pageContent } = usePageContent('editorial_board');
  const [editors, setEditors] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    let isMounted = true;
    getExecutiveEditors().then((data) => {
      if (isMounted) {
        setEditors(Array.isArray(data) ? data : []);
        setLoading(false);
      }
    }).catch(() => {
      if (isMounted) setLoading(false);
    });
    return () => {
      isMounted = false;
    };
  }, []);

  // 1. Always extract and prioritize Dr. Dheeraj Pandey for the top
  const dheerajMember = useMemo(() => {
    const fromDb = editors.find((e) => (e?.name || '').toLowerCase().includes('dheeraj'));
    if (fromDb) {
      return {
        name: fromDb.name,
        role: fromDb.designation || 'Editor-in-Chief',
        memberDesignation: fromDb.memberDesignation || fromDb.designationTitle || defaultEditorInChief.memberDesignation,
        designation: fromDb.memberDesignation || fromDb.qualification || defaultEditorInChief.designation,
        qualification: fromDb.qualification || defaultEditorInChief.qualification,
        address: fromDb.address || fromDb.institution || defaultEditorInChief.address,
        institution: fromDb.institution || defaultEditorInChief.institution,
        email: fromDb.email || defaultEditorInChief.email,
        photo: fromDb.img || defaultEditorInChief.photo,
        social: fromDb.social || [],
        profileLink: fromDb.profileLink || '',
      };
    }
    return defaultEditorInChief;
  }, [editors]);

  // 2. Extract all other editors from database
  const otherMembers = useMemo(() => {
    return editors
      .filter((e) => e && e.name && !(e.name || '').toLowerCase().includes('dheeraj'))
      .map((e) => ({
        name: e.name,
        role: e.designation || e.title || 'Editorial Board Member',
        memberDesignation: e.memberDesignation || e.designationTitle || '',
        designation: e.memberDesignation || e.qualification || '',
        qualification: e.qualification || '',
        address: e.address || e.institution || '',
        institution: e.institution || '',
        email: e.email || '',
        photo: e.img || '/assets/img/default-doctor.svg',
        social: e.social || [],
        profileLink: e.profileLink || '',
      }));
  }, [editors]);

  // 3. Build organized sections with Dr. Dheeraj Pandey ALWAYS at the top
  const sections = useMemo(() => {
    // Category 1: Editor-in-Chief (Always Dr. Dheeraj Pandey at the very top)
    const chiefMembers = [dheerajMember];

    // Dynamically group other members by their assigned category/designation
    const categoryMap = new Map();

    otherMembers.forEach((member) => {
      const roleTitle = (member.role || member.designation || 'Editorial Board Members').trim();
      if (!categoryMap.has(roleTitle)) {
        categoryMap.set(roleTitle, []);
      }
      categoryMap.get(roleTitle).push(member);
    });

    const result = [];

    if (chiefMembers.length > 0) {
      result.push({
        title: 'Editor-in-Chief',
        members: chiefMembers,
      });
    }

    // Append all categories dynamically created in the Admin panel
    categoryMap.forEach((members, title) => {
      result.push({
        title,
        members,
      });
    });

    return result;
  }, [dheerajMember, otherMembers]);

  if (isRawHtmlPage(pageContent)) {
    return <RawHtmlPage html={pageContent.full_content} />;
  }

  return (
    <div className="bg-[#FAF9F6] min-h-screen text-gray-800 font-sans pb-16">
      {/* Breadcrumb Bar */}
      <div className="bg-white py-3.5 px-4 sm:px-8 text-xs text-gray-500 shadow-xs">
        <div className="max-w-7xl mx-auto flex items-center gap-2">
          <Link to="/" className="text-[#03478E] hover:text-[#023366] hover:underline font-medium">
            Home
          </Link>
          <span>&gt;</span>
          <span className="font-semibold text-gray-700">Editorial Board</span>
        </div>
      </div>

      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-10 space-y-8">
        {/* Header Block */}
        <div className="pb-2">
          <h1 className="page-title">
            Editorial Board
          </h1>
        </div>

        {/* ================= EDITORIAL BOARD DIRECTORY ================= */}
        <section className="bg-white rounded-3xl overflow-hidden shadow-sm p-4 sm:p-6 space-y-8">
          {loading ? (
            <div className="p-8 text-center text-gray-500 text-sm animate-pulse">
              Loading editorial board...
            </div>
          ) : sections.length === 0 ? (
            <div className="p-8 text-center text-gray-500 text-sm">
              No editorial board members found.
            </div>
          ) : (
            sections.map((section) => (
              <div key={section.title} className="space-y-4">
                {/* Category Section Header */}
                <div className="bg-[#F8FAFC] border-l-4 border-[#03478E] px-5 py-3 rounded-r-2xl">
                  <h3 className="text-sm font-bold uppercase tracking-wider text-[#03478E]">
                    {section.title}
                  </h3>
                </div>

                {/* Member Directory Cards */}
                <div className="divide-y divide-gray-100">
                  {section.members.map((member, idx) => {
                    const designationText = member.memberDesignation || member.designation || member.qualification;
                    // Admin "Institutional Address" is saved as address, admin "Address" as institution
                    const institutionText = member.address || member.institution;
                    const addressText = member.address && member.institution && member.institution !== member.address
                      ? member.institution
                      : '';
                    const profileLinks = getProfileLinks(member);

                    return (
                      <div
                        key={member.name || idx}
                        className="py-6 px-3 sm:px-5 rounded-2xl transition-colors duration-200 hover:bg-slate-50/80 flex flex-col sm:flex-row items-start sm:items-stretch gap-5 sm:gap-7"
                      >
                        {/* Member Photo: portrait, stretches to the full height of the card */}
                        <div className="w-32 aspect-[4/5] sm:aspect-auto sm:w-40 sm:min-h-[10.5rem] sm:self-stretch rounded-xl overflow-hidden bg-slate-100 flex-shrink-0 ring-1 ring-gray-200/80 shadow-sm">
                          <img
                            src={member.photo || '/assets/img/default-doctor.svg'}
                            alt={member.name}
                            className="w-full h-full object-cover object-top"
                            onError={(e) => {
                              e.target.onerror = null;
                              e.target.src = '/assets/img/default-doctor.svg';
                            }}
                          />
                        </div>

                        {/* Vertical separator, fading out at the top and bottom */}
                        <div aria-hidden="true" className="hidden sm:block w-px self-stretch bg-gradient-to-b from-transparent via-gray-300 to-transparent" />

                        {/* Member Details: every line uses the same DetailRow style */}
                        <div className="flex-1 min-w-0 flex flex-col justify-center gap-2">
                          <h4 className="sub-heading !text-gray-900">{member.name}</h4>

                          {member.qualification && member.memberDesignation && member.qualification !== member.memberDesignation && (
                            <DetailRow icon={FaGraduationCap}>{member.qualification}</DetailRow>
                          )}
                          {designationText && <DetailRow icon={FaBriefcase}>{designationText}</DetailRow>}
                          {institutionText && <DetailRow icon={FaUniversity}>{institutionText}</DetailRow>}
                          {addressText && <DetailRow icon={FaMapMarkerAlt}>{addressText}</DetailRow>}

                          {profileLinks.length > 0 && (
                            <DetailRow icon={FaLink}>
                              <span className="flex flex-wrap gap-x-3 gap-y-1">
                                {profileLinks.map((link, linkIdx) => (
                                  <a key={linkIdx} href={link.url} target="_blank" rel="noopener noreferrer" className={DETAIL_LINK}>
                                    {link.label}
                                  </a>
                                ))}
                              </span>
                            </DetailRow>
                          )}

                          {member.email && (
                            <DetailRow icon={FaEnvelope}>
                              <a href={`mailto:${member.email}`} className={DETAIL_LINK}>{member.email}</a>
                            </DetailRow>
                          )}

                          {member.profileLink && (
                            <DetailRow icon={FaUserCircle}>
                              <a href={normalizeUrl(member.profileLink)} target="_blank" rel="noopener noreferrer" className={`${DETAIL_LINK} inline-flex items-center gap-1`}>
                                Profile link
                                <FaExternalLinkAlt className="w-2.5 h-2.5" />
                              </a>
                            </DetailRow>
                          )}
                        </div>
                      </div>
                    );
                  })}
                </div>
              </div>
            ))
          )}
        </section>
      </div>
    </div>
  );
};

export default EditorialBoardPage;
